/**
 * features/parent/ParentPushPrompt.tsx
 * ------------------------------------------------------------------
 * Turning on notifications for a parent, and letting them turn them off.
 *
 * WHY THIS EXISTS. Push was requested "on by default, with an opt-out". The
 * app half of that is easy and is exactly what this does. The browser half
 * cannot be done at all, by anybody:
 *
 *   Notification.requestPermission() must be called and the person must
 *   click Allow in a dialog the browser draws itself. There is no API, flag
 *   or setting that pre-grants it. On iOS Safari it is stricter still — web
 *   push only works once the site has been ADDED TO THE HOME SCREEN, so for
 *   a parent on an iPhone there is a step no code can take for them.
 *
 * So "default on" is delivered as: ASK PLAINLY, ONCE, WHERE THEY ARE, and
 * treat a grant as consent to everything until they say otherwise.
 *
 * Until now the only way in was an unlabelled bell in the staff header,
 * which parents share. 11 of 582 parent accounts had a device registered.
 * That is not parents declining; that is nobody being asked.
 *
 * WHAT IS DELIBERATE HERE:
 *
 *   - The prompt is dismissible and remembers the dismissal per device, so
 *     it is an offer rather than a wall. It comes back on the next device or
 *     after a reinstall, which is when it is useful again.
 *   - It is NOT auto-fired on load. A permission prompt with no context is
 *     dismissed reflexively, browsers penalise it, and a dismissal on some
 *     browsers is permanent — one careless prompt costs the channel forever.
 *   - Opting out sets `pushOptOut` on the user doc, honoured server-side in
 *     functions/lib/push.js for EVERY push, not per feature. The FCM token
 *     is left in place: the token is how the school COULD reach them, not
 *     permission to, and keeping it means switching back on is one tap
 *     rather than another browser dialog.
 * ------------------------------------------------------------------ */
import { useEffect, useState } from 'react';
import { BellRing, BellOff, X, Loader2, Check, Smartphone } from 'lucide-react';
import { doc } from 'firebase/firestore';
import { db, setDoc } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { enrollPush, pushAvailable, pushPermission } from '@/lib/push';

/** Per-device, so a dismissal on a phone does not silence the offer on a laptop. */
const DISMISS_KEY = 'lkc.pushPrompt.dismissed';

export default function ParentPushPrompt() {
  const { userData } = useAuth();
  const uid = userData?.uid;
  const optedOut = userData?.pushOptOut === true;

  const [available, setAvailable] = useState(false);
  const [perm, setPerm] = useState<NotificationPermission | 'unsupported'>('unsupported');
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      const ok = await pushAvailable();
      if (!live) return;
      setAvailable(ok);
      setPerm(pushPermission());
      // Already granted and not opted out: refresh the token silently so a
      // parent who cleared site data does not quietly stop receiving pushes.
      if (ok && uid && pushPermission() === 'granted' && !optedOut) {
        void enrollPush(uid, false);
      }
    })();
    return () => { live = false; };
  }, [uid, optedOut]);

  async function enable() {
    if (!uid) return;
    setBusy(true);
    setNote(null);
    try {
      // Clear any previous opt-out in the same action — "turn on" should mean
      // on, without a second step somewhere else.
      if (optedOut) await setDoc(doc(db, 'users', uid), { pushOptOut: false }, { merge: true });
      const res = await enrollPush(uid, true);
      setPerm(pushPermission());
      if (res === 'enrolled') setNote('Notifications are on for this device.');
      else if (res === 'denied') {
        setNote('Your browser is blocking notifications for this site. Allow them in your browser settings, then try again.');
      } else if (res === 'unsupported') {
        setNote('This browser cannot show notifications. On an iPhone, add the portal to your Home Screen first, then try again.');
      } else setNote('Not turned on. You can try again any time.');
    } catch {
      setNote('Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function turnOff() {
    if (!uid) return;
    setBusy(true);
    try {
      await setDoc(doc(db, 'users', uid), { pushOptOut: true }, { merge: true });
      setNote('Notifications are off. You will still get school emails.');
    } finally {
      setBusy(false);
    }
  }

  async function turnBackOn() {
    if (!uid) return;
    setBusy(true);
    try {
      await setDoc(doc(db, 'users', uid), { pushOptOut: false }, { merge: true });
      setNote('Notifications are back on.');
    } finally {
      setBusy(false);
    }
  }

  if (!uid || !available) return null;

  /* ---- ON: a quiet row with a way out, not a banner ---- */
  if (perm === 'granted') {
    return (
      <div className="flex items-center gap-2.5 text-xs text-gray-500 dark:text-gray-400 px-1">
        {optedOut ? <BellOff size={14} /> : <BellRing size={14} className="text-[#2d5295]" />}
        <span>
          {optedOut
            ? 'Notifications are off for this device.'
            : 'Notifications are on — the school can alert you here.'}
        </span>
        <button
          onClick={optedOut ? turnBackOn : turnOff}
          disabled={busy}
          className="ml-auto font-semibold text-[#2d5295] hover:underline disabled:opacity-60"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : optedOut ? 'Turn on' : 'Turn off'}
        </button>
      </div>
    );
  }

  if (perm === 'denied' || dismissed) return null;

  /* ---- NOT ASKED YET: the offer ---- */
  return (
    <div className="rounded-xl border border-[#d5e0f2] dark:border-[#2d5295]/40 bg-[#eef3fb] dark:bg-[#145374]/20 p-4">
      <div className="flex items-start gap-3">
        <BellRing size={18} className="text-[#2d5295] mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            Get notified when the school posts something
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 leading-relaxed">
            Reports, notices and letters from the school, the moment they arrive —
            no need to keep checking. You can turn this off any time.
          </p>

          {note && (
            <p className="text-xs mt-2 text-gray-700 dark:text-gray-200 bg-white/70 dark:bg-black/20 rounded-lg px-2.5 py-1.5">
              {note}
            </p>
          )}

          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <button
              onClick={enable}
              disabled={busy}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#2d5295] hover:bg-[#1b3a6b] text-white disabled:opacity-60"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
              Turn on notifications
            </button>
            <button
              onClick={() => {
                setDismissed(true);
                try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* private mode */ }
              }}
              className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 px-2 py-1.5"
            >
              Not now
            </button>
          </div>

          {/* iOS cannot do web push from a browser tab at all. Saying so up
              front beats a parent tapping and getting nothing. */}
          <p className="flex items-start gap-1.5 text-[11px] text-gray-500 dark:text-gray-400 mt-2.5">
            <Smartphone size={12} className="mt-0.5 shrink-0" />
            On an iPhone or iPad, add the portal to your Home Screen first — then turn this on.
          </p>
        </div>
        <button
          onClick={() => {
            setDismissed(true);
            try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* private mode */ }
          }}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 shrink-0"
          title="Dismiss"
        >
          <X size={15} />
        </button>
      </div>
    </div>
  );
}
